import { SendData } from '../utils/SendData';
import { COMPONENT_TYPE } from '../constants';

export class ImageManager {
  constructor({ url = null, urls = null, data = {}, startIndex = 0 } = {}) {
    this.url = url;
    this.data = data || {};
    this.images = Array.isArray(urls) ? urls.slice() : null;
    this.startIndex = Math.max(0, parseInt(startIndex || 0, 10));
    this._objectUrls = [];
    return this.open();
  }

  open() {
    return new Promise((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;
      this._createOverlay();

      if (this.images && this.images.length) {
        this._showImages(this.images, this.startIndex);
        return;
      }

      if (!this.url) {
        this._showError('URL не указан');
        return reject(new Error('URL not provided'));
      }

      // Быстрая проверка на явный файл изображения
      if (typeof this.url === 'string' && this.url.match(/\.(jpe?g|png|gif|webp|svg)(?:\?|$)/i)) {
        this._showImages([this.url], this.startIndex);
        return;
      }

      // Используем SendData для совместимости с общим ajax
      const uniq = 'imgreq-' + Date.now() + '-' + Math.random().toString(36).slice(2);
      const headers = { [COMPONENT_TYPE]: 'ImageManager', 'X-Image-Request-ID': uniq };

      new SendData({ url: this.url, data: this.data, type: 'POST', headers, messageAuto: false, loading: false })
        .then((response) => {
          const answer = response && response.answer;
          if (!answer) {
            this._showImages([this.url], this.startIndex);
            return;
          }

          if (answer instanceof Blob) {
            const obj = URL.createObjectURL(answer);
            this._objectUrls.push(obj);
            this._showImages([obj], 0);
            return;
          }

          if (typeof answer === 'string') {
            try {
              const js = JSON.parse(answer);
              if (Array.isArray(js)) { this._showImages(js, this.startIndex); return; }
              if (Array.isArray(js.images)) { this._showImages(js.images, this.startIndex); return; }
              if (js.image) { this._showImages([js.image], 0); return; }
            } catch (e) {
              const imgs = this._extractImagesFromHtml(answer);
              if (imgs.length) { this._showImages(imgs, this.startIndex); return; }
            }

            this._showImages([this.url], this.startIndex);
            return;
          }

          if (typeof answer === 'object') {
            if (Array.isArray(answer)) { this._showImages(answer, this.startIndex); return; }
            if (Array.isArray(answer.images)) { this._showImages(answer.images, this.startIndex); return; }
            if (answer.image) { this._showImages([answer.image], 0); return; }
            if (answer.answer && typeof answer.answer === 'string') {
              const imgs = this._extractImagesFromHtml(answer.answer);
              if (imgs.length) { this._showImages(imgs, this.startIndex); return; }
            }
          }

          this._showImages([this.url], this.startIndex);
        })
        .catch(() => {
          this._showImages([this.url], this.startIndex);
        });
    });
  }

  _createOverlay() {
    if (this._overlay) return;
    const overlay = document.createElement('div');
    overlay.className = 'image-manager-overlay';
    overlay.innerHTML = `
      <div class="im-backdrop"></div>
      <div class="im-wrap">
        <button class="im-close" aria-label="close">×</button>
        <div class="im-view">
          <button class="im-prev" aria-label="prev">‹</button>
          <div class="im-inner"><img class="im-img" src="" alt="image"></div>
          <button class="im-next" aria-label="next">›</button>
        </div>
        <div class="im-footer"><span class="im-counter"></span></div>
      </div>
    `;

    document.body.appendChild(overlay);
    this._overlay = overlay;
    this._img = overlay.querySelector('.im-img');
    this._inner = overlay.querySelector('.im-inner');
    this._prev = overlay.querySelector('.im-prev');
    this._next = overlay.querySelector('.im-next');
    this._close = overlay.querySelector('.im-close');
    this._counter = overlay.querySelector('.im-counter');

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onPrev = this._onPrev.bind(this);
    this._onNext = this._onNext.bind(this);
    this._onClose = this._onClose.bind(this);
    this._onBackdrop = this._onBackdrop.bind(this);

    this._prev.addEventListener('click', this._onPrev);
    this._next.addEventListener('click', this._onNext);
    this._close.addEventListener('click', this._onClose);
    overlay.querySelector('.im-backdrop').addEventListener('click', this._onBackdrop);

    // Zones for 40% / 20% / 40% click areas (left/right clickable zones)
    if (this._inner) {
      this._inner.classList.add('im-inner--with-zones');
      this._zoneLeft = document.createElement('div');
      this._zoneLeft.className = 'im-zone im-zone-left';
      this._zoneRight = document.createElement('div');
      this._zoneRight.className = 'im-zone im-zone-right';
      this._inner.appendChild(this._zoneLeft);
      this._inner.appendChild(this._zoneRight);

      // Click handlers for desktop/tap
      this._zoneLeft.addEventListener('click', (e) => { e.preventDefault(); this._onPrev(e); });
      this._zoneRight.addEventListener('click', (e) => { e.preventDefault(); this._onNext(e); });

      // Gesture (pointer) handlers for swipe on touch devices
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);

      this._inner.addEventListener('pointerdown', this._onPointerDown);
      document.addEventListener('pointermove', this._onPointerMove);
      document.addEventListener('pointerup', this._onPointerUp);
      this._img.draggable = false;
    }

    document.addEventListener('keydown', this._onKeyDown);
  }

  _extractImagesFromHtml(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const imgs = Array.from(doc.querySelectorAll('img')).map((i) => i.getAttribute('src')).filter(Boolean);
    // Приведём относительные ссылки к абсолютным, если возможно
    return imgs.map((src) => {
      try { return new URL(src, this.url).href; } catch (e) { return src; }
    });
  }

  _showImages(images, index = 0) {
    this.images = images.map((i) => (i === null ? '' : i));
    this.current = Math.max(0, Math.min(index, this.images.length - 1));
    this._updateControls();
    this._loadImage(this.current);
  }

  _loadImage(idx) {
    const src = this.images[idx];
    this._counter.textContent = `${idx + 1} / ${this.images.length}`;
    this._img.classList.remove('loaded', 'error');
    this._img.onload = () => {
      this._img.classList.add('loaded');
      this._updateControls();
    };
    this._img.onerror = () => {
      this._img.classList.add('error');
      this._updateControls();
    };
    this._img.src = src;
    // show/hide navigation
    if (this.images.length <= 1) {
      this._prev.style.display = 'none';
      this._next.style.display = 'none';
    } else {
      this._prev.style.display = '';
      this._next.style.display = '';
    }
  }

  _updateControls() {
    if (!this._overlay) return;
    this._prev.disabled = this.current <= 0;
    this._next.disabled = this.current >= this.images.length - 1;
  }

  _onPrev(e) {
    e && e.preventDefault();
    if (this.current > 0) {
      this.current -= 1;
      this._loadImage(this.current);
    }
  }

  _onNext(e) {
    e && e.preventDefault();
    if (this.current < this.images.length - 1) {
      this.current += 1;
      this._loadImage(this.current);
    }
  }

  _onClose(e) {
    e && e.preventDefault();
    this.close();
  }

  _onBackdrop(e) {
    if (e.target && e.target.classList.contains('im-backdrop')) this.close();
  }

  _onKeyDown(e) {
    if (!this._overlay) return;
    if (e.key === 'Escape') this.close();
    if (e.key === 'ArrowLeft') this._onPrev();
    if (e.key === 'ArrowRight') this._onNext();
  }

  _onPointerDown(e) {
    // only left mouse or touch/pointer
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this._pointerDown = true;
    this._startX = e.clientX;
    this._startY = e.clientY;
    this._lastX = e.clientX;
    this._isTouchPointer = e.pointerType === 'touch';
  }

  _onPointerMove(e) {
    if (!this._pointerDown) return;
    const dx = e.clientX - this._startX;
    const dy = e.clientY - this._startY;
    // ignore mostly vertical moves
    if (Math.abs(dy) > Math.abs(dx)) return;
    // visual feedback while swiping
    if (this._img) {
      this._img.style.transform = `translateX(${dx}px)`;
    }
  }

  _onPointerUp(e) {
    if (!this._pointerDown) return;
    this._pointerDown = false;
    const dx = e.clientX - this._startX;
    const rect = this._inner ? this._inner.getBoundingClientRect() : { width: window.innerWidth };
    const width = rect.width || window.innerWidth;
    const threshold = Math.max(40, width * 0.15); // 15% or 40px
    // reset transform
    if (this._img) {
      this._img.style.transition = 'transform 180ms ease';
      this._img.style.transform = '';
      setTimeout(() => { if (this._img) this._img.style.transition = ''; }, 200);
    }

    if (dx > threshold) {
      this._onPrev();
      return;
    }
    if (dx < -threshold) {
      this._onNext();
      return;
    }
  }

  _showError(text) {
    if (window.MessageManager) window.MessageManager.showMessage({ type: 'error', text });
  }

  close() {
    if (this._overlay) {
      this._overlay.remove();
      this._overlay = null;
    }
    document.removeEventListener('keydown', this._onKeyDown);
    // remove gesture listeners if attached
    try {
      if (this._zoneLeft) this._zoneLeft.remove();
      if (this._zoneRight) this._zoneRight.remove();
      if (this._inner) this._inner.removeEventListener('pointerdown', this._onPointerDown);
      document.removeEventListener('pointermove', this._onPointerMove);
      document.removeEventListener('pointerup', this._onPointerUp);
    } catch (e) {
      // ignore
    }
    // revoke object urls
    this._objectUrls.forEach((u) => URL.revokeObjectURL(u));
    this._objectUrls = [];
    if (this._resolve) this._resolve({ closed: true });
  }
}
